import React, { useState, useEffect, useRef } from 'react';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';

const fmtCurrency = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function CustomerPopover({ customerId, customerName, anchorRef, onClose, onViewHistory, theme = 'dark' }) {
  const T = getTheme(theme);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  // Position relative to anchor
  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popW = 280;
    const popH = 320; // estimated max height
    const left = Math.min(rect.left, window.innerWidth - popW - 16);
    // Flip upward if not enough space below
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < popH
      ? Math.max(8, rect.top - popH - 6)
      : rect.bottom + 6;
    setPos({ top, left });
  }, [anchorRef]);

  // Fetch summary
  useEffect(() => {
    if (!customerId || customerId === '0') {
      setError('No customer on file');
      setLoading(false);
      return;
    }
    fetch(`${API}/api/customers/${customerId}/summary`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setSummary(data); setLoading(false); })
      .catch(() => { setError('Could not load customer data'); setLoading(false); });
  }, [customerId]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, anchorRef]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const isDark = theme === 'dark';

  return (
    <div
      ref={popoverRef}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: 280,
        zIndex: 9999,
        background: isDark ? '#1e2a40' : '#ffffff',
        border: `1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
        borderRadius: 10,
        boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ padding: '12px 14px 10px', background: isDark ? '#243050' : '#f4f4ef', borderBottom: `1px solid ${isDark ? '#2e3d58' : '#e0e0d8'}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#e8e4dc' : '#1a1a1a', lineHeight: 1.3 }}>{customerName}</div>
          <div style={{ fontSize: 10, color: isDark ? '#5a6a8a' : '#999', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>Customer</div>
        </div>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: isDark ? '#5a6a8a' : '#aaa', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, marginTop: 2 }}>×</button>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px' }}>
        {loading ? (
          <div style={{ fontSize: 12, color: isDark ? '#5a6a8a' : '#999', textAlign: 'center', padding: '12px 0' }}>Loading…</div>
        ) : error ? (
          <div style={{ fontSize: 12, color: '#e74c3c', textAlign: 'center', padding: '8px 0' }}>{error}</div>
        ) : summary ? (
          <>
            {/* Contact */}
            {(summary.phone || summary.email) && (
              <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${isDark ? '#2e3d58' : '#e8e8e2'}` }}>
                {summary.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <i className="ti ti-phone" style={{ fontSize: 13, color: isDark ? '#5a6a8a' : '#aaa' }} />
                    <a href={`tel:${summary.phone}`} onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: '#3498db', textDecoration: 'none' }}>{summary.phone}</a>
                    <span style={{ fontSize: 10, color: isDark ? '#5a6a8a' : '#aaa' }}>{summary.phoneType}</span>
                  </div>
                )}
                {summary.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="ti ti-mail" style={{ fontSize: 13, color: isDark ? '#5a6a8a' : '#aaa' }} />
                    <span style={{ fontSize: 11, color: isDark ? '#8a9ab8' : '#555', wordBreak: 'break-all' }}>{summary.email}</span>
                  </div>
                )}
              </div>
            )}

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              {[
                { label: 'Total Visits',  value: summary.totalVisits },
                { label: 'Bikes on File', value: summary.totalBikes },
                { label: 'Total Spend',   value: fmtCurrency(summary.totalSpend), highlight: true },
                { label: 'First Visit',   value: fmtDate(summary.firstVisit) },
              ].map(({ label, value, highlight }) => (
                <div key={label} style={{ background: isDark ? '#243050' : '#f4f4ef', borderRadius: 6, padding: '8px 10px' }}>
                  <div style={{ fontSize: 10, color: isDark ? '#5a6a8a' : '#999', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: highlight ? '#2ecc71' : (isDark ? '#e8e4dc' : '#1a1a1a') }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Last visit */}
            <div style={{ fontSize: 11, color: isDark ? '#5a6a8a' : '#999', marginBottom: 12 }}>
              Last visit: <span style={{ color: isDark ? '#8a9ab8' : '#555' }}>{fmtDate(summary.lastVisit)}</span>
            </div>

            {/* View full history button */}
            <button
              onClick={() => { onViewHistory(summary); onClose(); }}
              style={{ width: '100%', padding: '8px 0', background: '#F4622A', border: 'none', borderRadius: 6, color: '#ffffff', fontSize: 12, fontWeight: 700, fontFamily: 'Arial, sans-serif', cursor: 'pointer', letterSpacing: '0.02em' }}>
              View Full History →
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
